import React from 'react';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { FormattedMessage } from 'react-intl';
import { Container, Button } from 'components/basics';
import IconsBlock from 'components/basics/Icons';
import { colors } from 'styles';

const StyledContainer = styled.div`
  margin-top: 15em;
  padding: ${({isSmall}) => !isSmall && `0 10%`};
`;

const BoldText = styled(Typography)`
  font-weight: bold;
`;

const Title = styled(BoldText)`
  color: ${colors.purple};
`;

const DataText = styled(BoldText)`
  color: ${colors.shades.purple.dark};
`;

const Price = styled(BoldText)`
  color: ${colors.lightBlue};
  margin-top: 0.25em;
`;

const Subtitle = styled(BoldText)`
  color: ${colors.grey};
`;
const CustomTableCell = withStyles(theme => ({
  head: {
    border: `2px solid ${colors.shades.grey.light}`,
    '&:nth-child(2)': {
      borderTopLeftRadius: '25%',
    },
    '&:last-child': {
      borderTopRightRadius: '25%',
    },
  },
  body: {
    border: `1px solid ${colors.shades.grey.light}`,
    color: colors.shades.purple.dark,
  },
}))(TableCell);

const CustomCell = styled(CustomTableCell)`
  & path {
    fill: ${({ isTrue }) => (isTrue ? colors.green : colors.red)};
  }
  & svg {
    border-radius: 50%;
    background-color: ${({ isTrue }) =>
      isTrue ? `${colors.green}33` : `${colors.red}33`};
    padding: 0.5em;
  }
`;

const CustomTableRow = withStyles(theme => ({
  head: {
    color: colors.shades.purple.dark,
  },
  root: {},
}))(TableRow);

const CustomRow = styled(CustomTableRow)`
  background: ${colors.shades.grey.light};
  padding: 1em;
`;

const CustomTableBody = withStyles(theme => ({
  root: {
    border: `2px solid ${colors.shades.grey.light}`,
    color: colors.shades.purple.dark,
  },
}))(TableBody);

const PlansTable = ({ isMonthlySelected, bodyRows, headerCells }) => {
  const breakpoints = useBreakpoint();
  let projectRows = [{ title: 'projects' }, ...bodyRows.slice(0, 5)];
  let featureRows = [{ title: 'features' }, ...bodyRows.slice(5, 14)];
  let fileImportRows = [{ title: 'fileImport' }, ...bodyRows.slice(14, 16)];
  let dataCollectionRows = [
    { title: 'dataCollectionTitle' },
    ...bodyRows.slice(16, 26),
  ];
  let supportRows = [{ title: 'support' }, ...bodyRows.slice(25 + 1)];

  const rows = [
    ...projectRows,
    ...featureRows,
    ...fileImportRows,
    ...dataCollectionRows,
    ...supportRows,
  ];

  const getSubtitle = cell => {
    if (cell.name === 'Emotion Corporate') {
      return <br />;
    } else if (cell.pricePerComment) {
      // return `${cell.pricePerComment}€ par commentaire`;
      return (
        <FormattedMessage
          id="pricing.detailedPricingBlock.perComment"
          values={{ price: `${parseFloat(cell.pricePerComment).toFixed(2)}€` }}
        />
      );
    } else if (isMonthlySelected) {
      return <FormattedMessage id="pricing.detailedPricingBlock.perMonth" />;
    } else {
      return (
        <FormattedMessage
          id="pricing.detailedPricingBlock.billedAt"
          values={{ price: `${cell.billedAnnualPrice}€` }}
        />
      );
    }
  };
  return (
    <StyledContainer isSmall={breakpoints.md}>
      <Table aria-label="pricing table" padding="dense">
        <TableHead>
          <CustomTableRow>
            <TableCell />
            {headerCells.map(cell => (
              <CustomCell align="center">
                <Title variant="body1">{cell.name}</Title>
                <Price variant="body1">
                  {isMonthlySelected ? cell.monthlyPrice : cell.annualPrice}
                  {cell.billedAnnualPrice ? '€' : ''}
                </Price>
                <Subtitle variant="caption">{getSubtitle(cell)}</Subtitle>
                {cell.name === 'Emotion Corporate' ? (
                  <Button
                    color="primary"
                    customTextColor={colors.white}
                    customBackgroundColor={colors.lightBlue}
                    customHoverBackgroundColor={colors.shades.lightBlue.dark}
                    customHoverTextColor={colors.white}
                    variant="contained"
                    mt={2}
                  >
                    <FormattedMessage id="pricing.contactUs" />
                  </Button>
                ) : (
                  <Button
                    color="primary"
                    customTextColor={colors.white}
                    customBackgroundColor={colors.lightBlue}
                    customHoverBackgroundColor={colors.shades.lightBlue.dark}
                    customHoverTextColor={colors.white}
                    variant="contained"
                    mt={2}
                    onClick={() => window.open('https://www.cxinsights.io/')}
                  >
                    <FormattedMessage id="pricing.choosePlan" />
                  </Button>
                )}
              </CustomCell>
            ))}
          </CustomTableRow>
        </TableHead>
        <CustomTableBody>
          {rows.map(row =>
            row.title ? (
              <CustomRow>
                <CustomCell colSpan={5}>
                  <Title variant="body1">
                    <FormattedMessage
                      id={`pricing.detailedPricingBlock.${row.title}`}
                    />
                  </Title>
                </CustomCell>
              </CustomRow>
            ) : (
              <CustomTableRow>
                <CustomTableCell>
                  <FormattedMessage
                    id={`pricing.detailedPricingBlock.${row.legend}`}
                  />
                </CustomTableCell>
                <CustomCell align="center" isTrue={row['Emotion Start']}>
                  <DataText variant="caption">
                    {typeof row['Emotion Start'] === 'boolean' ? (
                      <IconsBlock
                        icon={row['Emotion Start'] ? 'check' : 'cross'}
                        isTrue={row['Emotion Start']}
                        widthIcon={32}
                        heightIcon={32}
                      />
                    ) : (
                      row['Emotion Start']
                    )}
                  </DataText>
                </CustomCell>
                {/*<CustomCell align="center" isTrue={row['Emotion Plus']}>*/}
                {/*  <DataText variant="caption">*/}
                {/*    {typeof row['Emotion Plus'] === 'boolean' ? (*/}
                {/*      <IconsBlock*/}
                {/*        icon={row['Emotion Plus'] ? 'check' : 'cross'}*/}
                {/*        isTrue={row['Emotion Plus']}*/}
                {/*        widthIcon={32}*/}
                {/*        heightIcon={32}*/}
                {/*      />*/}
                {/*    ) : (*/}
                {/*      row['Emotion Plus']*/}
                {/*    )}*/}
                {/*  </DataText>*/}
                {/*</CustomCell>*/}
                <CustomCell align="center" isTrue={row['Emotion Pro']}>
                  <DataText variant="caption">
                    {typeof row['Emotion Pro'] === 'boolean' ? (
                      <IconsBlock
                        icon={row['Emotion Pro'] ? 'check' : 'cross'}
                        isTrue={row['Emotion Pro']}
                        widthIcon={32}
                        heightIcon={32}
                      />
                    ) : (
                      row['Emotion Pro']
                    )}
                  </DataText>
                </CustomCell>
                <CustomCell align="center" isTrue={row['Emotion Corporate']}>
                  <DataText variant="caption">
                    {typeof row['Emotion Corporate'] === 'boolean' ? (
                      <IconsBlock
                        icon={row['Emotion Corporate'] ? 'check' : 'cross'}
                        isTrue={row['Emotion Corporate']}
                        widthIcon={32}
                        heightIcon={32}
                      />
                    ) : (
                      row['Emotion Corporate'] || 'Illimité'
                    )}
                  </DataText>
                </CustomCell>
              </CustomTableRow>
            )
          )}
        </CustomTableBody>
      </Table>
    </StyledContainer>
  );
};

export default PlansTable;
